import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import { Property } from '@propertypal/shared/src/types/property';
import { filterPropertiesByPhase, getPropertyPhaseOptions } from '@propertypal/shared/src/utils/property/propertyPhase';
import { SORT_OPTIONS, sortProperties } from '@propertypal/shared/src/utils/property/propertySort';
import Button from '@propertypal/web-ui/src/buttons/Button';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import SimilarProperties from '@propertypal/web-ui/src/property/SimilarProperties';
import PropertyBox from '@propertypal/web-ui/src/property-box/PropertyBox';
import { SubHeading } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useMemo } from 'react';
import { PROPERTY_IMAGE_LOCATOR } from './PropertyDetails';
import { Container, DropdownContainer, Header, PinnedPropertyContainer } from './PropertyTypesCarousel.style';

interface Props {
  properties: Property[];
  phase: string;
  setPhase: (phase: string) => void;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  onViewAll: () => void;
}

const PropertyTypesCarousel: FunctionComponent<Props> = (props) => {
  const [sort, setSort] = useLocalStorage<string>('PROPERTY_TYPE_SORT', 'default');

  const phaseOptions = useMemo(() => getPropertyPhaseOptions(props.properties), [props.properties]);

  const isMobile = useMediaQuery({ query: `(max-width: 700px)` });

  const { pinnedProperty, sorted } = useMemo(() => {
    const filteredProperties = filterPropertiesByPhase(props.properties, props.phase);
    const pinned = filteredProperties?.find((p) => p?.pinned);

    const sortedProperties = sortProperties(filteredProperties?.filter((p) => p.id !== pinned?.id), sort);

    return {
      pinnedProperty: !isMobile && pinned,
      sorted: isMobile && pinned ? [pinned, ...sortedProperties] : sortedProperties,
    };
  }, [props.phase, props.properties, sort, isMobile]);

  return (
    <Container data-testid="property-types-carousel">
      <Header $hasPinned={!!pinnedProperty}>
        <SubHeading>Property Types</SubHeading>

        <DropdownContainer>
          {phaseOptions.length > 0 && (
            <SelectInput
              containerStyle={{ maxWidth: 260 }}
              ariaLabel="phase"
              id="property-types-phase-select"
              testID="property-types-phase-select"
              value={props.phase}
              onChange={props.setPhase}
              options={phaseOptions}
              placeholder="Select Phase"
            />
          )}

          <SelectInput
            containerStyle={{ maxWidth: 260 }}
            ariaLabel="sort by"
            id="property-types-sort-select"
            testID="property-types-sort-select"
            value={sort}
            onChange={setSort}
            options={SORT_OPTIONS}
          />
        </DropdownContainer>
      </Header>

      {pinnedProperty && (
        <PinnedPropertyContainer data-testid="pinned-property-type">
          <PropertyBox
            property={{ ...pinnedProperty, listingTier: 'STANDARD' }}
            onFavourite={props.onFavourite}
            useWideDisplay
            pinnedPropertyType
            hideFooter
            getReplaceUrl={(nextSection: string) => `${pinnedProperty?.path}${nextSection}`}
          />
        </PinnedPropertyContainer>
      )}

      <SimilarProperties
        properties={sorted}
        imageLocator={PROPERTY_IMAGE_LOCATOR[1]}
        onFavourite={props.onFavourite}
        background="transparent"
        showArrows
        displayPinned={!!isMobile}
      />

      {props.properties.length > 1 && (
        <Button testID="view-all-property-types" onClick={props.onViewAll}>
          View All Property Types
        </Button>
      )}
    </Container>
  );
};

export default PropertyTypesCarousel;
